import { useState, type ChangeEvent, type FocusEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { AppointmentInfo } from '../../ui-module/appointment-info.js';
import { useDobValidation } from '../../token-module/use-dob-validation.js';
import type { AppointmentInfo as AppointmentInfoData } from '../../token-module/appointment-info.interface.js';
import type { DobError } from '../../token-module/dob-error.js';

const DOB_ERROR_KEYS: Readonly<Record<DobError, string>> = {
  required: 'error.dob.required',
  future: 'error.dob.future',
  tooOld: 'error.dob.tooOld',
};

const DOB_ERROR_ID = 'start-dob-error';
const DOB_HINT_ID = 'start-dob-hint';
const SUBMIT_HINT_ID = 'start-submit-hint';

const INPUT_BASE =
  'min-h-touch w-full rounded-2xl border bg-card px-4 py-3 text-base text-foreground shadow-sm outline-none transition-all focus:ring-2';
const INPUT_OK = 'border-border focus:border-brand focus:ring-brand/20';
const INPUT_ERROR = 'border-error focus:border-error focus:ring-error/20';

const SUBMIT_BASE =
  'min-h-touch w-full rounded-2xl px-4 py-3.5 text-base font-semibold transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';
const SUBMIT_OK =
  'bg-brand text-brand-foreground shadow-md shadow-brand/20 hover:brightness-110 active:scale-[0.99]';
const SUBMIT_DISABLED =
  'bg-brand text-brand-foreground opacity-60 cursor-not-allowed shadow-sm shadow-brand/10';

interface StartScreenProps {
  readonly appointmentInfo?: AppointmentInfoData;
  readonly onSubmit?: (dob: string) => void;
}

/**
 * Start-Screen mit Termininformation, DOB-Eingabe (Native `type="date"` —
 * Browser liefert den plattform-eigenen Picker und Indicator, damit die
 * Eingabe sich für Patient:innen auf Mobile/Desktop nativ und gewohnt
 * anfühlt) und primärem Submit-CTA im Brand-Look.
 *
 * Funktion unverändert: `useDobValidation` gibt den ISO-String,
 * `onSubmit` läuft nur bei valider Eingabe.
 *
 * Inline-Fehler erscheinen erst nach Blur (oder Submit-Versuch). Beim Tippen
 * eines mehrstelligen Datums (z. B. Jahr Zeichen für Zeichen) entstehen
 * sonst Zwischenzustände, in denen `useDobValidation` `tooOld`/`future`
 * meldet — die Fehlermeldung würde aufblinken und den Submit-Button unter
 * sich verschieben. Der Error-Container reserviert zusätzlich Platz, damit
 * spätere Fehlerwechsel keinen Layout-Shift auslösen.
 */
export function StartScreen(props: StartScreenProps = {}): JSX.Element {
  const { t } = useTranslation();
  const dob = useDobValidation();
  const [isTouched, setIsTouched] = useState(false);

  function handleDobChange(event: ChangeEvent<HTMLInputElement>): void {
    dob.setValue(event.target.value);
  }

  function handleDobBlur(_event: FocusEvent<HTMLInputElement>): void {
    setIsTouched(true);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setIsTouched(true);
    if (dob.isValid && props.onSubmit !== undefined) {
      props.onSubmit(dob.value);
    }
  }

  const isErrorVisible = isTouched && dob.error !== null;
  return (
    <div className="flex w-full flex-col gap-8">
      <AppointmentInfo {...(props.appointmentInfo ? { info: props.appointmentInfo } : {})} />
      <form noValidate onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
        <DobField
          value={dob.value}
          onChange={handleDobChange}
          onBlur={handleDobBlur}
          minDate={dob.minDate}
          maxDate={dob.maxDate}
          isErrorVisible={isErrorVisible}
        />
        <div className="-mt-2 min-h-[1.25rem]" aria-live="polite">
          {isErrorVisible && dob.error !== null ? (
            <p id={DOB_ERROR_ID} role="alert" className="text-sm text-error">
              {t(DOB_ERROR_KEYS[dob.error])}
            </p>
          ) : null}
        </div>
        <SubmitButton
          isValid={dob.isValid}
          label={t('start.submit')}
          disabledHint={t('start.submitDisabledHint')}
        />
      </form>
    </div>
  );
}

/**
 * Natives Geburtsdatum-Eingabefeld (`<input type="date">`). Browser liefern
 * eigene Picker-UIs und Indicator-Icons — auf iOS, Android, Desktop sieht
 * und reagiert das Feld so, wie es die Plattform für Date-Inputs vorsieht.
 * Kein Overlay, kein CSS-Trick: damit bleibt die Eingabe für Patient:innen
 * vertraut, unabhängig vom Gerät.
 *
 * Min/Max sind die Klick-Grenzen im Native-Picker; die programmatische
 * Validierung kommt von `useDobValidation` und ist die kanonische Quelle.
 *
 * Der Format-Hinweis (`birthdateFormatHint`) bleibt für Screenreader im
 * `sr-only`-Span erhalten — die plattform-eigene Date-Input-UI hat
 * üblicherweise ihren eigenen Format-Hinweis.
 */
function DobField(props: {
  readonly value: string;
  readonly onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly onBlur: (event: FocusEvent<HTMLInputElement>) => void;
  readonly minDate: string;
  readonly maxDate: string;
  readonly isErrorVisible: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const inputClassName = `${INPUT_BASE} ${props.isErrorVisible ? INPUT_ERROR : INPUT_OK}`;
  const describedBy = props.isErrorVisible ? `${DOB_HINT_ID} ${DOB_ERROR_ID}` : DOB_HINT_ID;
  return (
    <label className="flex flex-col gap-2 text-sm">
      <span className="font-medium text-foreground">{t('start.birthdateLabel')}</span>
      <input
        type="date"
        autoComplete="bday"
        required
        min={props.minDate}
        max={props.maxDate}
        value={props.value}
        onChange={props.onChange}
        onBlur={props.onBlur}
        aria-invalid={props.isErrorVisible}
        aria-describedby={describedBy}
        className={inputClassName}
      />
      <span id={DOB_HINT_ID} className="sr-only">
        {t('start.birthdateFormatHint')}
      </span>
    </label>
  );
}

/**
 * Submit-Button. Disabled bei invaliden Eingaben; der Disabled-Zustand wird
 * via `aria-describedby` zusätzlich an einen sr-only Hint geknüpft.
 */
function SubmitButton(props: {
  readonly isValid: boolean;
  readonly label: string;
  readonly disabledHint: string;
}): JSX.Element {
  const className = `${SUBMIT_BASE} ${props.isValid ? SUBMIT_OK : SUBMIT_DISABLED}`;
  return (
    <>
      <button
        type="submit"
        disabled={!props.isValid}
        aria-disabled={!props.isValid}
        aria-describedby={!props.isValid ? SUBMIT_HINT_ID : undefined}
        className={className}
      >
        {props.label}
      </button>
      {!props.isValid ? (
        <span id={SUBMIT_HINT_ID} className="sr-only">
          {props.disabledHint}
        </span>
      ) : null}
    </>
  );
}
